import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { CheckCircleIcon, XCircleIcon, PlusIcon, TrashIcon, SparklesIcon } from '@heroicons/react/24/outline'

interface SkillCategory {
  value: string
  label: string
}

interface AICuratorPrompt {
  role: string
  language: string
  courseStructure: string
  testingLogic: string
  safetyRules: string
  privateClub: string
  templates: string
}

interface Settings {
  integrations?: {
    ai: {
      provider: string
      model: string
      apiKey: string
      enabled: boolean
      maxTokens?: number
      temperature?: number
      curatorPrompt?: AICuratorPrompt
    }
    amocrm: {
      domain: string
      apiKey: string
      enabled: boolean
    }
  }
  gamification?: {
    pointsPerLesson: number
    pointsPerQuiz: number
    pointsPerPractice: number
    pointsPerDay: number
    streakBonus: boolean
    pointsForPremium: number
  }
  privateClub?: {
    links: {
      createAccountUrl: string
      confirmChatUrl: string
      confirmChatUseDeepLink?: boolean
    }
  }
  skillCategories?: {
    categories: SkillCategory[]
  }
}

const defaultSkillCategories: SkillCategory[] = [
  { value: 'trading_basics', label: 'Trading Basics' },
  { value: 'risk_management', label: 'Risk Management' },
  { value: 'trading_psychology', label: 'Trading Psychology' },
  { value: 'technical_analysis', label: 'Technical Analysis' },
  { value: 'choosing_broker', label: 'Choosing Broker' },
]

const defaultCuratorPrompt: AICuratorPrompt = {
  role: `You are an AI trading education curator. Your main tasks:
1. Test students' knowledge after completing each level
2. Answer free questions about trading
3. Support and motivate students

You speak in a friendly, encouraging but professional manner. You know trading theory, psychology, risk management, and technical analysis.`,
  
  language: `Always respond in Russian language unless specifically asked to respond in another language. Use informal "ты" form.`,
  
  courseStructure: `The course consists of levels (Stage), each level contains lessons:
- Stage 01: Introduction - basic concepts
- Stage 02: How Market Works - market mechanics
- Stage 03: Trading Psychology - emotions and discipline
- Stage 04: Advanced Strategies (Private Club only) - advanced methods`,
  
  testingLogic: `When testing students:
1. You have access to the level content in system context
2. Ask 3-5 questions about the level material
3. Questions should be practical and scenario-based, not rote memorization
4. Accept answers in any form - paraphrasing, examples, explanations
5. If answer is close to correct (70%+ understanding), count it as correct
6. At the end, give overall assessment and motivating feedback`,
  
  safetyRules: `IMPORTANT restrictions:
1. Do NOT give specific buy/sell signals
2. Do NOT guarantee profit results
3. Do NOT discuss specific broker deposits or amounts
4. If asked about something unrelated to trading/education - politely redirect to course topics
5. Do NOT provide personal financial advice`,
  
  privateClub: `For Private Club verification:
1. User may say they deposited money with a broker
2. You should guide them to use the "Confirm deposit" button in the app
3. Explain that verification is automatic through CRM
4. Do NOT manually "unlock" premium content`,
  
  templates: `Response templates:
- Level test start: "Отлично, давай проверим что ты усвоил! Я задам тебе несколько вопросов..."
- Correct answer: "Верно! 👍" + brief explanation why
- Partially correct: "Почти! Ты на верном пути..." + guidance
- Wrong answer: "Не совсем так. Давай разберём..." + explanation
- Test passed: "Поздравляю! 🎉 Ты успешно прошёл тест по этому уровню!"
- Test failed: "Пока не получилось. Рекомендую пересмотреть урок [X] и попробовать снова."`
}

export default function SettingsPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('integrations')
  const [aiTestResult, setAiTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [newSkillValue, setNewSkillValue] = useState('')
  const [newSkillLabel, setNewSkillLabel] = useState('')

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<{ data: { settings: Settings } }>('/admin/settings')
      return res.data.data.settings
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      await api.put(`/admin/settings/${key}`, value)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('Настройки сохранены')
    },
    onError: () => {
      toast.error('Ошибка сохранения')
    },
  })

  const testAiMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ data: { success: boolean; message: string } }>('/admin/settings/test-ai')
      return res.data.data
    },
    onSuccess: (data) => {
      setAiTestResult(data)
      if (data.success) {
        toast.success('AI подключение работает')
      } else {
        toast.error('Ошибка подключения AI')
      }
    },
    onError: () => {
      setAiTestResult({ success: false, message: 'Ошибка тестирования' })
    },
  })

  const handleSubmit = (key: string) => (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const value: any = {}

    formData.forEach((v, k) => {
      if (k.includes('.')) {
        const parts = k.split('.')
        let current = value
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {}
          current = current[parts[i]]
        }
        current[parts[parts.length - 1]] = v === 'on' ? true : v
      } else {
        value[k] = v === 'on' ? true : v
      }
    })

    // Handle checkboxes that are unchecked
    const form = e.currentTarget
    form.querySelectorAll('input[type="checkbox"]').forEach((input: any) => {
      const name = input.name
      if (name.includes('.')) {
        const parts = name.split('.')
        let current = value
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {}
          current = current[parts[i]]
        }
        if (current[parts[parts.length - 1]] === undefined) current[parts[parts.length - 1]] = false
      } else {
        if (value[name] === undefined) value[name] = false
      }
    })

    // Handle number fields
    form.querySelectorAll('input[type="number"]').forEach((input: any) => {
      const name = input.name
      if (name.includes('.')) {
        const parts = name.split('.')
        let current = value
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {}
          current = current[parts[i]]
        }
        current[parts[parts.length - 1]] = parseFloat(input.value) || 0
      } else {
        value[name] = parseFloat(input.value) || 0
      }
    })

    updateMutation.mutate({ key, value })
  }

  // Функции для управления скилами
  const currentSkillCategories = settings?.skillCategories?.categories || defaultSkillCategories

  const handleAddSkill = () => {
    if (!newSkillValue.trim() || !newSkillLabel.trim()) {
      toast.error('Заполните оба поля')
      return
    }

    // Проверяем на дубликаты
    if (currentSkillCategories.some(s => s.value === newSkillValue.trim())) {
      toast.error('Скилл с таким значением уже существует')
      return
    }

    const newCategories = [
      ...currentSkillCategories,
      { value: newSkillValue.trim().toLowerCase().replace(/\s+/g, '_'), label: newSkillLabel.trim() }
    ]

    updateMutation.mutate({
      key: 'skillCategories',
      value: { categories: newCategories }
    })

    setNewSkillValue('')
    setNewSkillLabel('')
  }

  const handleDeleteSkill = (value: string) => {
    if (currentSkillCategories.length <= 1) {
      toast.error('Нельзя удалить последний скилл')
      return
    }

    if (!confirm('Удалить этот скилл?')) return

    const newCategories = currentSkillCategories.filter(s => s.value !== value)
    updateMutation.mutate({
      key: 'skillCategories',
      value: { categories: newCategories }
    })
  }

  // AI Curator prompt
  const currentCuratorPrompt = settings?.integrations?.ai?.curatorPrompt || defaultCuratorPrompt

  const handleResetPrompt = (field: keyof AICuratorPrompt) => {
    if (!confirm('Сбросить это поле к значению по умолчанию?')) return
    
    const form = document.querySelector('form[data-form="aiCurator"]') as HTMLFormElement
    if (form) {
      const textarea = form.querySelector(`textarea[name="ai.curatorPrompt.${field}"]`) as HTMLTextAreaElement
      if (textarea) {
        textarea.value = defaultCuratorPrompt[field]
      }
    }
  }

  const tabs = [
    { id: 'integrations', name: 'Интеграции' },
    { id: 'aiCurator', name: 'AI Куратор', icon: SparklesIcon },
    { id: 'gamification', name: 'Геймификация' },
    { id: 'privateClub', name: 'Private Club' },
    { id: 'skillCategories', name: 'Скилы' },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <h1 className="text-2xl font-bold text-white">Настройки</h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-4 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.name}
          </button>
        ))}
      </div>

      {/* Integrations */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* AI */}
          <form
            key={`integrations-${JSON.stringify(settings?.integrations?.ai)}`}
            onSubmit={handleSubmit('integrations')}
            className="card space-y-4"
          >
            <h3 className="text-lg font-medium text-white">AI (для проверки квизов и куратора)</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Провайдер
                </label>
                <select
                  name="ai.provider"
                  defaultValue={settings?.integrations?.ai?.provider || 'openrouter'}
                  className="w-full"
                >
                  <option value="openrouter">OpenRouter</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Модель
                </label>
                <input
                  type="text"
                  name="ai.model"
                  defaultValue={settings?.integrations?.ai?.model || 'openai/gpt-4o-mini'}
                  className="w-full"
                  placeholder="openai/gpt-4o-mini"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key
              </label>
              <input
                type="password"
                name="ai.apiKey"
                key={`ai-api-key-${settings?.integrations?.ai?.apiKey ?? 'empty'}`}
                defaultValue={settings?.integrations?.ai?.apiKey || ''}
                placeholder="sk-..."
                className="w-full max-w-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                {settings?.integrations?.ai?.apiKey ? 'Ключ сохранён. Оставьте как есть или введите новый' : 'Введите API ключ'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Tokens
                </label>
                <input
                  type="number"
                  name="ai.maxTokens"
                  defaultValue={settings?.integrations?.ai?.maxTokens || 2000}
                  min="100"
                  max="8000"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Temperature
                </label>
                <input
                  type="number"
                  name="ai.temperature"
                  defaultValue={settings?.integrations?.ai?.temperature || 0.7}
                  min="0"
                  max="2"
                  step="0.1"
                  className="w-full"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="ai.enabled"
                defaultChecked={settings?.integrations?.ai?.enabled}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-300">Включить AI</span>
            </label>

            <div className="flex items-center gap-4">
              <button type="submit" className="btn-primary">
                Сохранить
              </button>
              <button
                type="button"
                onClick={() => testAiMutation.mutate()}
                disabled={testAiMutation.isPending}
                className="btn-secondary"
              >
                {testAiMutation.isPending ? 'Тестирование...' : 'Тест подключения'}
              </button>

              {aiTestResult && (
                <div className={`flex items-center gap-2 ${aiTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {aiTestResult.success ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    <XCircleIcon className="w-5 h-5" />
                  )}
                  <span className="text-sm">{aiTestResult.message}</span>
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      {/* AI Curator */}
      {activeTab === 'aiCurator' && (
        <form
          data-form="aiCurator"
          onSubmit={handleSubmit('integrations')}
          className="space-y-6"
        >
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-yellow-500" />
                  Промпты AI Куратора
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Настройте поведение AI куратора для обучения и тестирования студентов
                </p>
              </div>
            </div>

            {/* Role */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Роль и миссия
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('role')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.role"
                defaultValue={currentCuratorPrompt.role}
                rows={5}
                className="w-full font-mono text-sm"
                placeholder="Опишите роль AI куратора..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Основное описание роли, задач и характера AI куратора
              </p>
            </div>

            {/* Language */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Язык общения
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('language')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.language"
                defaultValue={currentCuratorPrompt.language}
                rows={2}
                className="w-full font-mono text-sm"
                placeholder="На каком языке и в какой форме общаться..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Язык ответов, форма обращения (ты/вы)
              </p>
            </div>

            {/* Course Structure */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Структура курса
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('courseStructure')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.courseStructure"
                defaultValue={currentCuratorPrompt.courseStructure}
                rows={5}
                className="w-full font-mono text-sm"
                placeholder="Опишите структуру курса..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Информация о структуре курса, уровнях и уроках
              </p>
            </div>

            {/* Testing Logic */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Логика тестирования
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('testingLogic')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.testingLogic"
                defaultValue={currentCuratorPrompt.testingLogic}
                rows={6}
                className="w-full font-mono text-sm"
                placeholder="Как AI должен тестировать студентов..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Правила проведения тестов: количество вопросов, критерии оценки, формат
              </p>
            </div>

            {/* Safety Rules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Правила безопасности
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('safetyRules')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.safetyRules"
                defaultValue={currentCuratorPrompt.safetyRules}
                rows={5}
                className="w-full font-mono text-sm"
                placeholder="Ограничения и запреты..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Что AI НЕ должен делать: финансовые советы, гарантии прибыли и т.д.
              </p>
            </div>

            {/* Private Club */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Private Club
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('privateClub')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.privateClub"
                defaultValue={currentCuratorPrompt.privateClub}
                rows={4}
                className="w-full font-mono text-sm"
                placeholder="Инструкции по Private Club..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Как AI должен обрабатывать вопросы о Private Club и верификации депозита
              </p>
            </div>

            {/* Templates */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  Шаблоны ответов
                </label>
                <button
                  type="button"
                  onClick={() => handleResetPrompt('templates')}
                  className="text-xs text-gray-500 hover:text-gray-300"
                >
                  Сбросить
                </button>
              </div>
              <textarea
                name="ai.curatorPrompt.templates"
                defaultValue={currentCuratorPrompt.templates}
                rows={8}
                className="w-full font-mono text-sm"
                placeholder="Шаблоны типичных ответов..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Примеры фраз для начала теста, правильных/неправильных ответов, завершения
              </p>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t border-gray-700">
              <button type="submit" className="btn-primary">
                Сохранить все промпты
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Сбросить ВСЕ промпты к значениям по умолчанию?')) {
                    updateMutation.mutate({
                      key: 'integrations',
                      value: {
                        ...settings?.integrations,
                        ai: {
                          ...settings?.integrations?.ai,
                          curatorPrompt: defaultCuratorPrompt
                        }
                      }
                    })
                  }
                }}
                className="btn-secondary text-red-400 hover:text-red-300"
              >
                Сбросить все к умолчанию
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Gamification */}
      {activeTab === 'gamification' && (
        <form onSubmit={handleSubmit('gamification')} className="card space-y-4">
          <h3 className="text-lg font-medium text-white">Геймификация</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Баллов за урок
              </label>
              <input
                type="number"
                name="pointsPerLesson"
                min="0"
                defaultValue={settings?.gamification?.pointsPerLesson || 10}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Баллов за квиз
              </label>
              <input
                type="number"
                name="pointsPerQuiz"
                min="0"
                defaultValue={settings?.gamification?.pointsPerQuiz || 15}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Баллов за практику
              </label>
              <input
                type="number"
                name="pointsPerPractice"
                min="0"
                defaultValue={settings?.gamification?.pointsPerPractice || 20}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Баллов за день
              </label>
              <input
                type="number"
                name="pointsPerDay"
                min="0"
                defaultValue={settings?.gamification?.pointsPerDay || 5}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Баллов для Premium уровней
            </label>
            <input
              type="number"
              name="pointsForPremium"
              min="0"
              defaultValue={settings?.gamification?.pointsForPremium || 500}
              className="w-full max-w-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Количество баллов, необходимое для разблокировки Premium уровней
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="streakBonus"
              defaultChecked={settings?.gamification?.streakBonus}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-gray-300">Бонус за streak</span>
          </label>

          <button type="submit" className="btn-primary">
            Сохранить
          </button>
        </form>
      )}

      {/* Private Club */}
      {activeTab === 'privateClub' && (
        <form onSubmit={handleSubmit('privateClub')} className="card space-y-4">
          <h3 className="text-lg font-medium text-white">Ссылки Private Club</h3>
          <p className="text-sm text-gray-400">
            Эти ссылки отображаются в мобильном приложении на экране Private Club
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ссылка "Create real account"
              </label>
              <input
                type="url"
                name="links.createAccountUrl"
                defaultValue={settings?.privateClub?.links?.createAccountUrl || ''}
                placeholder="https://broker-partner.com/register"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ссылка на регистрацию у брокера-партнёра
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ссылка "Confirm in chat"
              </label>
              <input
                type="url"
                name="links.confirmChatUrl"
                defaultValue={settings?.privateClub?.links?.confirmChatUrl || ''}
                placeholder="https://t.me/support_chat"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ссылка на чат для подтверждения (Telegram, WhatsApp и т.д.)
              </p>
              <label className="flex items-center gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  name="links.confirmChatUseDeepLink"
                  defaultChecked={settings?.privateClub?.links?.confirmChatUseDeepLink}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-300">Открывать AI чат в приложении</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Если включено, пользователи сразу попадают в AI чат приложения, внешняя ссылка не используется.
              </p>
            </div>
          </div>

          <button type="submit" className="btn-primary">
            Сохранить
          </button>
        </form>
      )}

      {/* Skill Categories */}
      {activeTab === 'skillCategories' && (
        <div className="card space-y-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Управление скилами</h3>
            <p className="text-sm text-gray-400">
              Добавляйте и удаляйте категории скилов для уроков. Эти категории используются при создании уроков.
            </p>
          </div>

          {/* Добавление нового скилла */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-3">Добавить новый скилл</h4>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Значение (value)
                </label>
                <input
                  type="text"
                  value={newSkillValue}
                  onChange={(e) => setNewSkillValue(e.target.value)}
                  placeholder="fundamental_analysis"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Латиница, без пробелов (используйте _)
                </p>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Название (label)
                </label>
                <input
                  type="text"
                  value={newSkillLabel}
                  onChange={(e) => setNewSkillLabel(e.target.value)}
                  placeholder="Fundamental Analysis"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Отображаемое имя
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddSkill}
                disabled={updateMutation.isPending}
                className="btn-primary h-10"
              >
                <PlusIcon className="w-5 h-5 mr-1" />
                Добавить
              </button>
            </div>
          </div>

          {/* Список существующих скилов */}
          <div>
            <h4 className="text-sm font-medium text-white mb-3">Текущие скилы ({currentSkillCategories.length})</h4>
            <div className="space-y-2">
              {currentSkillCategories.map((skill) => (
                <div
                  key={skill.value}
                  className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-white font-medium">{skill.label}</p>
                      <p className="text-xs text-gray-500">{skill.value}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteSkill(skill.value)}
                    disabled={updateMutation.isPending}
                    className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    title="Удалить"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
